import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/**
 * LoginForm - Parameterized login screen for admin portal apps.
 * @param {object} props
 * @param {function} props.onLogin - async (credentials) => { success: bool, error?: string }
 * @param {function} [props.onGoogleLogin] - async (idToken) => { success: bool, error?: string }
 * @param {string} [props.googleClientId] - Google OAuth Web client ID
 * @param {string} props.appName - App display name (e.g. "Recipe Genie Admin")
 * @param {React.ReactNode} props.logo - Logo element to render above the title
 * @param {string} props.brandColor - Tailwind color class for buttons (e.g. "orange" → from-orange-500)
 * @param {string} [props.tagline] - Optional tagline below the app name
 */
const LoginForm = ({ onLogin, onGoogleLogin, googleClientId, appName, logo, brandColor = 'blue', tagline }) => {
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const googleBtnRef = useRef(null);

  useEffect(() => {
    if (!googleClientId || !onGoogleLogin) return;

    const initGoogle = () => {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response) => {
          setIsLoading(true);
          setLoginError('');
          const result = await onGoogleLogin(response.credential);
          if (!result.success) setLoginError(result.error || 'Google sign-in failed');
          setIsLoading(false);
        },
      });
      if (googleBtnRef.current) {
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          width: googleBtnRef.current.offsetWidth || 400,
          text: 'signin_with',
        });
      }
    };

    if (window.google?.accounts?.id) {
      initGoogle();
    } else {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initGoogle;
      document.head.appendChild(script);
    }
  }, [googleClientId, onGoogleLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoading(true);
    try {
      const result = await onLogin(loginForm);
      if (!result.success) {
        setLoginError(result.error || 'Login failed');
      }
    } catch (error) {
      setLoginError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Map brand color to gradient classes
  const gradients = {
    orange: 'from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 focus:ring-orange-500',
    blue: 'from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 focus:ring-blue-500',
    green: 'from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 focus:ring-green-500',
    pink: 'from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:ring-pink-500',
    purple: 'from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 focus:ring-purple-500',
  };
  const gradient = gradients[brandColor] || gradients.blue;

  const focusRingMap = {
    orange: 'focus:ring-orange-500',
    blue: 'focus:ring-blue-500',
    green: 'focus:ring-green-500',
    pink: 'focus:ring-pink-500',
    purple: 'focus:ring-purple-500',
  };
  const focusRing = focusRingMap[brandColor] || focusRingMap.blue;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 p-4 py-8 flex items-center justify-center">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
        <div className="text-center mb-8">
          <div className="flex flex-col items-center justify-center mb-4 mt-4">
            {logo && <div className="mb-4">{logo}</div>}
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
              {appName}
            </h1>
            {tagline && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{tagline}</p>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Username</label>
            <input
              type="text"
              value={loginForm.username}
              onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
              className={`w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 ${focusRing} focus:border-transparent`}
              placeholder="Enter username"
              autoComplete="username"
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={loginForm.password}
                onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                className={`w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 ${focusRing} focus:border-transparent`}
                placeholder="Enter password"
                autoComplete="current-password"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {loginError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{loginError}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full bg-gradient-to-r ${gradient} text-white py-3 rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl`}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {googleClientId && onGoogleLogin && (
          <div className="mt-4">
            <div className="relative flex items-center my-4">
              <div className="flex-grow border-t border-gray-200 dark:border-gray-600" />
              <span className="mx-3 text-xs text-gray-400 dark:text-gray-500">or</span>
              <div className="flex-grow border-t border-gray-200 dark:border-gray-600" />
            </div>
            <div ref={googleBtnRef} className="flex justify-center" />
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginForm;
